import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, ChevronDown, Files, Plus } from 'lucide-react';
import { PAGE_LIMIT, type SceneStore } from '@os/doc';
import { cn, focusRing, Menu } from '../../ui';
import { Icon } from '../../ui/icons';
import { pageLabel } from './labels';
import type { PageNav } from './page-nav';

/**
 * The page chip of the title bar (P14): the active page's name, and a menu of every page (the active one checked),
 * « Nouvelle page » for editors, « Toutes les pages… » (the panel) and « ← Page précédente » after a page link. A
 * reader of a one-page board has nothing to choose: no chip.
 */
export function PageSwitcher({ store, nav, onOpenPanel }: { store: SceneStore; nav: PageNav; onOpenPanel: () => void }) {
  const { t } = useTranslation();
  const pages = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePages(cb), [store]), () => store.pages());
  const page = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePage(cb), [store]), () => store.page);
  const readOnly = useSyncExternalStore(useCallback((cb: () => void) => store.subscribeReadOnly(cb), [store]), () => store.readOnly);
  const canGoBack = useSyncExternalStore(useCallback((cb: () => void) => nav.subscribe(cb), [nav]), () => nav.canGoBack);
  if (readOnly && pages.length < 2) return null;
  const index = Math.max(0, pages.findIndex((p) => p.id === page));
  const current = pageLabel(t, pages[index]!, index);
  const add = () => {
    const id = store.commands.addPage({ after: page });
    if (id) nav.go(id);
  };
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={`${t('pages.switcher')}: ${current}`}
        data-page-switcher=""
        className={cn(
          'flex h-8 max-w-48 cursor-pointer items-center gap-1 rounded-full px-2.5 font-sans text-sm text-fg hover:bg-chrome-hover data-[popup-open]:bg-chrome-hover pointer-coarse:h-11',
          focusRing,
        )}
      >
        <Icon icon={Files} size={14} className="shrink-0 text-fg-muted" />
        <span className="truncate">{current}</span>
        <Icon icon={ChevronDown} size={14} className="shrink-0 text-fg-muted" />
      </Menu.Trigger>
      <Menu.Popup label={t('pages.title')} className="max-h-[60dvh] overflow-auto">
        {canGoBack && (
          <>
            <Menu.Item onClick={() => nav.back()}>
              <Icon icon={ArrowLeft} size={14} />
              {t('pages.back')}
            </Menu.Item>
            <Menu.Separator />
          </>
        )}
        {pages.map((p, i) => (
          <Menu.Item key={p.id} role="menuitemradio" aria-checked={p.id === page} onClick={() => nav.go(p.id)}>
            <span className="grid w-4 shrink-0 place-items-center">{p.id === page && <Icon icon={Check} size={14} />}</span>
            <span className="truncate">{pageLabel(t, p, i)}</span>
          </Menu.Item>
        ))}
        <Menu.Separator />
        {!readOnly && (
          <Menu.Item disabled={pages.length >= PAGE_LIMIT} onClick={add}>
            <Icon icon={Plus} size={14} />
            {t('pages.new')}
          </Menu.Item>
        )}
        <Menu.Item onClick={onOpenPanel}>{t('pages.all')}</Menu.Item>
      </Menu.Popup>
    </Menu.Root>
  );
}
